import React, { useState, useEffect, useCallback } from 'react';
import { FaEdit, FaTrash, FaTag, FaEye, FaStore, FaMagic, FaRedo } from 'react-icons/fa';
import { useLocation as useRouterLocation } from 'react-router-dom';
import { warehousePricingService } from '../services/warehousePricingService';
import { cenowkiService } from '../services/cenowkiService';
import { productService } from '../services/productService';
import manufacturerService from '../services/manufacturerService';
import toast from 'react-hot-toast';

import {
  PricingFilters,
  PricingTable,
  PriceChangeModal,
  DirectPriceModal,
  PriceHistoryModal,
  PriceLabelsPreview,
  BufferPanel,
  BulkEditModal,
  ManufacturerModal,
  usePricingData,
  useBarcodeScanner,
  useLabelBuffer,
} from '../components/pricing';

import {
  simplifyProductName,
  extractPackageQuantity,
  extractWeight,
  extractManufacturer,
  extractUnitAndQuantity,
  getDisplayPrice,
  calculatePricePerUnit,
  calculatePriceFromUnit,
  computeUnitPriceInfo,
} from '../components/pricing/pricingUtils';

const LocationPricingPage = () => {
  const routerLocation = useRouterLocation();

  // --- Custom hooks ---
  const pricingData = usePricingData();
  const {
    selectedLocation, availableLocations, changeLocation, user,
    canEditPrices, canPrintLabels, canViewHistory, canDeleteSpecialPrice,
    loading, setLoading, error, setError,
    pokazBezStanu, setPokazBezStanu,
    allProducts, categories, manufacturers, measureUnits, warehouseId,
    invoices, selectedInvoiceId, invoiceEANs, invoiceLoading, handleInvoiceFilter,
    loadAllProducts, refreshAllData, loadManufacturers, loadUnits,
  } = pricingData;

  // Checkbox "Dostepne": przelaczenie od razu przeladowuje liste z serwera
  // (available_only=1/0). Bez tego odznaczenie nic by nie dalo - lista jest
  // filtrowana lokalnie, a produktow bez stanu w niej po prostu nie ma.
  const przelaczBezStanu = useCallback((wartosc) => {
    setPokazBezStanu(wartosc);
    loadAllProducts(false, { pokazBezStanu: wartosc });
  }, [setPokazBezStanu, loadAllProducts]);

  const labelBufferHook = useLabelBuffer({
    selectedLocation, user, manufacturers, measureUnits, routerLocation
  });
  const {
    labelBuffer, showBuffer, setShowBuffer, loadBuffer,
    addToBuffer, removeFromBuffer, clearBuffer,
    labelSettings, setLabelSettings, settingsSaved, handleSaveSettings,
    showPreview, setShowPreview, previewProducts, setPreviewProducts,
    copyMultiplier, setCopyMultiplier, selectedForCopy, setSelectedForCopy,
    handleToggleCopySelection, handleSelectAllForCopy, handleDeselectAllForCopy,
    handleTransferBufferToPreview, handleLoadBufferToPreview,
  } = labelBufferHook;

  // --- Local UI state ---
  const [selectedProducts, setSelectedProducts] = useState(new Set());
  const [filterTerm, setFilterTerm] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState({
    priceType: 'all', marginFilter: 'all', marginThreshold: 15,
    // Zakres marzy w %: puste = brak ograniczenia z tej strony
    marginMin: '', marginMax: ''
  });
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedManufacturer, setSelectedManufacturer] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [sortColumn, setSortColumn] = useState('nazwa');
  const [sortDirection, setSortDirection] = useState('asc');
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [editingProductManufacturer, setEditingProductManufacturer] = useState(null);

  // Cenowka inline editor state
  const [editingCenowkaProductId, setEditingCenowkaProductId] = useState(null);
  const [selectedProductForCenowka, setSelectedProductForCenowka] = useState(null);
  const [cenowkaEditData, setCenowkaEditData] = useState({
    nazwa_uproszczona: '', cena_cenowkowa: '', cena_promocyjna: '',
    typ_cenowki: 'standardowa', kategoria_cenowki: '', opis_cenowki: '',
    waga: '', jednostka_wagi: 'nieustawiono'
  });
  const [showPriceCalculator, setShowPriceCalculator] = useState(true);
  const [calculatedPrices, setCalculatedPrices] = useState({ per100g: 0, perUnit: 0, basePrice: 0 });

  // Price modals state
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [priceChangeMode, setPriceChangeMode] = useState('fixed');
  const [priceChangeValue, setPriceChangeValue] = useState('');
  const [roundToPsychological, setRoundToPsychological] = useState(false);

  const [showDirectPriceModal, setShowDirectPriceModal] = useState(false);
  const [selectedProductForDirectEdit, setSelectedProductForDirectEdit] = useState(null);
  const [directPriceNetto, setDirectPriceNetto] = useState('');
  const [directPriceBrutto, setDirectPriceBrutto] = useState('');

  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedProductForHistory, setSelectedProductForHistory] = useState(null);
  const [priceHistory, setPriceHistory] = useState([]);

  const [showManufacturerModal, setShowManufacturerModal] = useState(false);
  const [bulkManufacturerChange, setBulkManufacturerChange] = useState('');
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkUnitChange, setBulkUnitChange] = useState('');
  const [bulkQuantityChange, setBulkQuantityChange] = useState('');

  // Barcode scanner hook
  const scannerHook = useBarcodeScanner({
    allProducts, selectedProducts, setSelectedProducts, selectedLocation, labelBuffer, loadBuffer
  });
  const {
    scannedBarcodes, barcodeInput, setBarcodeInput,
    scannerFilterActive, setScannerFilterActive,
    scannerInputRef, handleBarcodeInput, handlePasteBarcodes,
    handleRemoveBarcode, handleClearAllBarcodes, handleAddScannedToBuffer,
  } = scannerHook;

  // --- Close dropdown menu on outside click ---
  useEffect(() => {
    const handleClickOutside = () => setOpenActionMenu(null);
    if (openActionMenu !== null) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openActionMenu]);

  // --- Calculate per-unit prices ---
  useEffect(() => {
    const { cena_cenowkowa, waga, jednostka_wagi } = cenowkaEditData;
    if (cena_cenowkowa && waga && parseFloat(waga) > 0) {
      const perUnit = calculatePricePerUnit(cena_cenowkowa, waga, jednostka_wagi);
      setCalculatedPrices({
        per100g: jednostka_wagi === 'gramy' || jednostka_wagi === 'ml' ? perUnit : 0,
        perUnit: jednostka_wagi === 'tabletki' || jednostka_wagi === 'kapsułki' || jednostka_wagi === 'sztuki' ? perUnit : 0,
        basePrice: parseFloat(cena_cenowkowa)
      });
    } else {
      setCalculatedPrices({ per100g: 0, perUnit: 0, basePrice: 0 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cenowkaEditData.cena_cenowkowa, cenowkaEditData.waga, cenowkaEditData.jednostka_wagi]);

  const setPriceFromCalculator = (unitPrice, targetWeight) => {
    if (!targetWeight || parseFloat(targetWeight) <= 0) {
      toast.error('Wprowadź prawidłową wagę/ilość');
      return;
    }
    const newPrice = calculatePriceFromUnit(unitPrice, targetWeight, cenowkaEditData.jednostka_wagi);
    setCenowkaEditData(prev => ({ ...prev, cena_cenowkowa: newPrice.toFixed(2), waga: targetWeight }));
  };

  // --- Filtering and sorting ---
  const getFilteredProducts = useCallback(() => {
    return allProducts.filter(product => {
      // Scanner filter
      if (scannerFilterActive && scannedBarcodes.length > 0) {
        const productEAN = product.ean?.trim().toLowerCase() || '';
        const productCode = product.kod_produktu?.trim().toLowerCase() || '';
        const matchesScanned = scannedBarcodes.some(code => {
          const lowerCode = code.toLowerCase();
          return productEAN === lowerCode || productCode === lowerCode;
        });
        if (!matchesScanned) return false;
      }

      // Invoice filter
      if (invoiceEANs.size > 0) {
        const productEAN = product.ean?.trim() || '';
        const productCode = product.kod_produktu?.trim() || '';
        if (!invoiceEANs.has(productEAN) && !invoiceEANs.has(productCode)) return false;
      }

      // Text search (AND logic)
      if (filterTerm) {
        const words = filterTerm.toLowerCase().split(/\s+/).filter(w => w.length > 0);
        const productText = [product.nazwa || '', product.kod_produktu || '', product.ean || '', product.producent || ''].join(' ').toLowerCase();
        if (!words.every(word => productText.includes(word))) return false;
      }

      if (selectedCategory && product.kategoria !== selectedCategory) return false;
      if (selectedManufacturer && product.producent_id !== parseInt(selectedManufacturer)) return false;
      if (advancedFilters.priceType === 'special' && !product.hasSpecialPrice) return false;
      if (advancedFilters.priceType === 'default' && product.hasSpecialPrice) return false;

      if (advancedFilters.marginFilter === 'below') {
        // To samo zrodlo marzy co kolumna w tabeli (displayMarginPercent z hooka)
        const currentMargin = product.displayMarginPercent;
        if (currentMargin === null || currentMargin === undefined || currentMargin >= advancedFilters.marginThreshold) return false;
      }

      // Zakres marzy od-do (%): to samo zrodlo co kolumna w tabeli.
      // Produkty bez policzonej marzy (null, np. kaucje/brak kosztu) odpadaja,
      // gdy ktorykolwiek kraniec zakresu jest ustawiony.
      const hasMin = advancedFilters.marginMin !== '' && !isNaN(parseFloat(advancedFilters.marginMin));
      const hasMax = advancedFilters.marginMax !== '' && !isNaN(parseFloat(advancedFilters.marginMax));
      if (hasMin || hasMax) {
        const m = product.displayMarginPercent;
        if (m === null || m === undefined) return false;
        if (hasMin && m < parseFloat(advancedFilters.marginMin)) return false;
        if (hasMax && m > parseFloat(advancedFilters.marginMax)) return false;
      }

      return true;
    }).sort((a, b) => {
      let valueA, valueB;
      switch (sortColumn) {
        case 'nazwa':
          valueA = (a.nazwa || '').toLowerCase(); valueB = (b.nazwa || '').toLowerCase(); break;
        case 'producent':
          valueA = (a.producent || a.manufacturer_name || '').toLowerCase(); valueB = (b.producent || b.manufacturer_name || '').toLowerCase(); break;
        case 'kod':
          valueA = (a.kod_produktu || a.ean || '').toLowerCase(); valueB = (b.kod_produktu || b.ean || '').toLowerCase(); break;
        case 'opakowanie':
          valueA = a.ilosc_jednostek || parseInt(extractPackageQuantity(a, a.nazwa, a.opis, measureUnits)) || 0;
          valueB = b.ilosc_jednostek || parseInt(extractPackageQuantity(b, b.nazwa, b.opis, measureUnits)) || 0; break;
        case 'cena_zakupu':
          valueA = parseFloat(a.purchase_price) || 0; valueB = parseFloat(b.purchase_price) || 0; break;
        case 'cena_sprzedazy':
          valueA = parseFloat(a.hasSpecialPrice ? a.specialPriceBrutto : a.cena_sprzedazy_brutto) || 0;
          valueB = parseFloat(b.hasSpecialPrice ? b.specialPriceBrutto : b.cena_sprzedazy_brutto) || 0; break;
        case 'marza':
          // To samo zrodlo marzy co kolumna w tabeli (displayMarginPercent z hooka)
          valueA = a.displayMarginPercent ?? 0;
          valueB = b.displayMarginPercent ?? 0; break;
        default:
          valueA = (a.nazwa || '').toLowerCase(); valueB = (b.nazwa || '').toLowerCase();
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortDirection === 'asc' ? valueA.localeCompare(valueB, 'pl') : valueB.localeCompare(valueA, 'pl');
      }
      return sortDirection === 'asc' ? valueA - valueB : valueB - valueA;
    });
  }, [allProducts, scannerFilterActive, scannedBarcodes, invoiceEANs, filterTerm, selectedCategory, selectedManufacturer, advancedFilters, sortColumn, sortDirection, measureUnits]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleSelectProduct = (productId) => {
    const newSelected = new Set(selectedProducts);
    if (newSelected.has(productId)) { newSelected.delete(productId); } else { newSelected.add(productId); }
    setSelectedProducts(newSelected);
  };

  const handleSelectAll = () => {
    const filteredProducts = getFilteredProducts();
    if (selectedProducts.size === filteredProducts.length) {
      setSelectedProducts(new Set());
    } else {
      setSelectedProducts(new Set(filteredProducts.map(p => p.id)));
    }
  };

  // --- Cenowka handlers ---
  const handleEditCenowka = async (product) => {
    loadManufacturers(); loadUnits();

    if (editingCenowkaProductId === product.id) {
      setEditingCenowkaProductId(null); setSelectedProductForCenowka(null); return;
    }
    if (!selectedLocation) { toast.error('Proszę najpierw wybrać magazyn'); return; }

    setEditingCenowkaProductId(product.id);
    setSelectedProductForCenowka(product);

    try {
      const locationIdVal = selectedLocation?.id || selectedLocation;
      const cenowkaResponse = await cenowkiService.getCenowkaByProduct(product.id, locationIdVal);
      const existingCenowka = cenowkaResponse?.data;
      if (existingCenowka) {
        let detectedUnit = existingCenowka.jednostka_wagi;
        let detectedQuantity = existingCenowka.waga;
        if (!detectedUnit || detectedUnit === 'nieustawiono' || detectedUnit === 'gramy') {
          const extracted = extractUnitAndQuantity(product.nazwa, product.opis, measureUnits);
          if (extracted.unit !== 'nieustawiono') {
            detectedUnit = extracted.unit;
            if (!detectedQuantity && extracted.quantity) detectedQuantity = extracted.quantity;
          }
        }
        setCenowkaEditData({
          nazwa_uproszczona: existingCenowka.nazwa_uproszczona || '',
          cena_cenowkowa: existingCenowka.cena_cenowkowa || '',
          cena_promocyjna: existingCenowka.cena_promocyjna || '',
          typ_cenowki: existingCenowka.typ_cenowki || 'standardowa',
          kategoria_cenowki: existingCenowka.kategoria_cenowki || extractManufacturer(product.nazwa, product.opis, manufacturers),
          opis_cenowki: existingCenowka.opis_cenowki || '',
          waga: detectedQuantity || '', jednostka_wagi: detectedUnit || 'nieustawiono'
        });
      } else {
        const extracted = extractUnitAndQuantity(product.nazwa, product.opis, measureUnits);
        const detectedMfr = extractManufacturer(product.nazwa, product.opis, manufacturers);
        setCenowkaEditData({
          nazwa_uproszczona: simplifyProductName(product.nazwa, manufacturers) || product.nazwa || '',
          cena_cenowkowa: product.hasSpecialPrice ? product.specialPriceBrutto : product.cena_sprzedazy_brutto || '',
          cena_promocyjna: '', typ_cenowki: 'standardowa', kategoria_cenowki: detectedMfr,
          opis_cenowki: '', waga: extracted.quantity || '', jednostka_wagi: extracted.unit || 'nieustawiono'
        });
      }
    } catch (err) {
      const extracted = extractUnitAndQuantity(product.nazwa, product.opis, measureUnits);
      const detectedMfr = extractManufacturer(product.nazwa, product.opis, manufacturers);
      setCenowkaEditData({
        nazwa_uproszczona: simplifyProductName(product.nazwa, manufacturers) || product.nazwa || '',
        cena_cenowkowa: product.specialPrice || product.cena_sprzedazy_brutto || '',
        cena_promocyjna: '', typ_cenowki: 'standardowa', kategoria_cenowki: detectedMfr,
        opis_cenowki: '', waga: extracted.quantity || '', jednostka_wagi: extracted.unit || 'nieustawiono'
      });
    }
  };

  const handleSaveCenowka = async () => {
    if (!selectedProductForCenowka || !cenowkaEditData.nazwa_uproszczona || !cenowkaEditData.cena_cenowkowa) {
      toast.error('Uzupełnij wymagane pola: nazwę uproszczoną i cenę'); return;
    }
    if (!selectedLocation) { toast.error('Nie wybrano magazynu'); return; }

    try {
      await cenowkiService.createOrUpdateCenowka({
        product_id: selectedProductForCenowka.id,
        location_id: selectedLocation.id,
        nazwa_uproszczona: cenowkaEditData.nazwa_uproszczona,
        cena_cenowkowa: parseFloat(cenowkaEditData.cena_cenowkowa),
        cena_promocyjna: cenowkaEditData.cena_promocyjna ? parseFloat(cenowkaEditData.cena_promocyjna) : null,
        typ_cenowki: cenowkaEditData.typ_cenowki,
        kategoria_cenowki: cenowkaEditData.kategoria_cenowki || null,
        opis_cenowki: cenowkaEditData.opis_cenowki || null,
        waga: cenowkaEditData.waga ? parseFloat(cenowkaEditData.waga) : null,
        jednostka_wagi: cenowkaEditData.jednostka_wagi !== 'nieustawiono' ? cenowkaEditData.jednostka_wagi : null,
        aktywny: true
      });
      await loadAllProducts(true);
      setEditingCenowkaProductId(null); setSelectedProductForCenowka(null);
      toast.success('Cenówka została zapisana!');
    } catch (err) {
      toast.error('Błąd podczas zapisywania cenówki: ' + err.message);
    }
  };

  const handleCancelCenowkaEdit = () => {
    setEditingCenowkaProductId(null); setSelectedProductForCenowka(null);
  };

  // --- Price change handlers ---
  const handleBulkPriceChange = () => {
    if (selectedProducts.size === 0) { toast.error('Nie wybrano żadnych produktów'); return; }
    setPriceChangeValue(''); setPriceChangeMode('fixed'); setRoundToPsychological(false);
    setShowPriceModal(true);
  };

  const applyPriceChange = async () => {
    if (!priceChangeValue || isNaN(priceChangeValue)) { toast.error('Podaj prawidłową wartość'); return; }
    if (!warehouseId) { toast.error('Brak warehouse_id - wybierz lokalizację'); return; }

    const changeValue = parseFloat(priceChangeValue);
    const selectedProductsList = getFilteredProducts().filter(p => selectedProducts.has(p.id));
    if (selectedProductsList.length === 0) { toast.error('Nie znaleziono produktów do aktualizacji'); return; }

    try {
      setLoading(true);
      let successCount = 0, errorCount = 0;

      for (const product of selectedProductsList) {
        try {
          let newPriceNetto;
          const currentPriceNetto = product.hasSpecialPrice ? product.specialPriceNetto : product.cena_sprzedazy_netto;

          switch (priceChangeMode) {
            case 'amount': newPriceNetto = currentPriceNetto + changeValue; break;
            case 'percent': newPriceNetto = currentPriceNetto * (1 + changeValue / 100); break;
            case 'margin': {
              const purchasePriceNetto = product.purchase_price || product.cena_zakupu_netto || product.cena_zakupu || 0;
              if (!purchasePriceNetto || purchasePriceNetto <= 0) { errorCount++; continue; }
              const targetMargin = changeValue / 100;
              if (targetMargin >= 1) { errorCount++; continue; }
              newPriceNetto = purchasePriceNetto / (1 - targetMargin); break;
            }
            case 'fixed': {
              const vatRateFixed = (product.stawka_vat || 23) / 100;
              newPriceNetto = changeValue / (1 + vatRateFixed); break;
            }
            default: continue;
          }

          if (newPriceNetto <= 0) continue;

          const vatRate = (product.stawka_vat || 23) / 100;
          let newPriceBrutto = priceChangeMode === 'fixed' ? changeValue : newPriceNetto * (1 + vatRate);
          if (roundToPsychological) {
            newPriceBrutto = Math.floor(newPriceBrutto) + 0.99;
            newPriceNetto = newPriceBrutto / (1 + vatRate);
          }
          newPriceNetto = Math.round(newPriceNetto * 100) / 100;
          newPriceBrutto = Math.round(newPriceBrutto * 100) / 100;

          const response = await warehousePricingService.setWarehousePrice(warehouseId, product.id, {
            cena_sprzedazy_netto: newPriceNetto, cena_sprzedazy_brutto: newPriceBrutto, created_by: "user_bulk"
          });
          if (response.success) successCount++; else errorCount++;
        } catch (e) { errorCount++; }
      }

      await refreshAllData();
      setShowPriceModal(false); setPriceChangeValue(''); setSelectedProducts(new Set());
      if (successCount > 0) toast.success(`Pomyślnie zaktualizowano ceny ${successCount} produktów${errorCount > 0 ? `, błędów: ${errorCount}` : ''}`);
      else toast.error('Nie udało się zaktualizować żadnej ceny');
    } catch (err) {
      setError(`Błąd podczas aktualizacji cen: ${err.message}`);
    } finally { setLoading(false); }
  };

  const handleRemoveSpecialPrices = async () => {
    if (selectedProducts.size === 0) { toast.error('Nie wybrano żadnych produktów'); return; }
    if (!window.confirm(`Czy na pewno chcesz usunąć ceny specjalne dla ${selectedProducts.size} produktów?`)) return;
    try {
      setLoading(true);
      for (const productId of selectedProducts) {
        await warehousePricingService.removeWarehousePrice(selectedLocation.id, productId);
      }
      await refreshAllData(); setSelectedProducts(new Set());
    } catch (err) { setError(err.message); } finally { setLoading(false); }
  };

  const handleShowHistory = async (product) => {
    try {
      setSelectedProductForHistory(product); setLoading(true);
      const response = await warehousePricingService.getPriceHistory(selectedLocation.id, product.id);
      setPriceHistory(response.success ? (response.data || []) : []);
      setShowHistoryModal(true);
    } catch (err) {
      setPriceHistory([]); setShowHistoryModal(true);
    } finally { setLoading(false); }
  };

  const handleDirectPriceEdit = (product) => {
    setSelectedProductForDirectEdit(product);
    if (product.hasSpecialPrice) {
      setDirectPriceNetto(product.specialPriceNetto?.toString() || '');
      setDirectPriceBrutto(product.specialPriceBrutto?.toString() || '');
    } else {
      setDirectPriceNetto(product.cena_sprzedazy_netto?.toString() || '');
      setDirectPriceBrutto(product.cena_sprzedazy_brutto?.toString() || '');
    }
    setShowDirectPriceModal(true);
  };

  const applyDirectPriceChange = async () => {
    if (!directPriceNetto || !directPriceBrutto || isNaN(directPriceNetto) || isNaN(directPriceBrutto)) {
      toast.error('Podaj prawidłowe ceny netto i brutto'); return;
    }
    const newPriceNetto = parseFloat(directPriceNetto);
    const newPriceBrutto = parseFloat(directPriceBrutto);
    if (newPriceNetto <= 0 || newPriceBrutto <= 0) { toast.error('Ceny muszą być większe od zera'); return; }
    if (!warehouseId) { toast.error('Nie znaleziono magazynu dla tej lokalizacji.'); return; }

    try {
      setLoading(true);
      const response = await warehousePricingService.setWarehousePrice(warehouseId, selectedProductForDirectEdit.id, {
        cena_sprzedazy_netto: newPriceNetto, cena_sprzedazy_brutto: newPriceBrutto, created_by: "user_manual"
      });
      if (response.success) {
        await refreshAllData();
        setShowDirectPriceModal(false); setDirectPriceNetto(''); setDirectPriceBrutto(''); setSelectedProductForDirectEdit(null);
        toast.success('Cena została pomyślnie zaktualizowana');
      } else { toast.error(`Błąd aktualizacji ceny: ${response.error || 'Nieznany błąd'}`); }
    } catch (err) { setError(`Błąd podczas aktualizacji ceny: ${err.message}`); } finally { setLoading(false); }
  };

  // Inline edycja ceny specjalnej (dwuklik na komorce w tabeli). Edytujemy BRUTTO,
  // netto wyliczamy ze stawki VAT produktu (VAT<=0, np. kaucja -2: netto == brutto).
  // Zapis ta sama sciezka co modal (setWarehousePrice -> synchronizacja magazynow lokalizacji).
  const handleInlineSpecialPriceSave = async (product, newBruttoRaw) => {
    const newBrutto = parseFloat(String(newBruttoRaw).replace(',', '.'));
    if (isNaN(newBrutto) || newBrutto <= 0) { toast.error('Podaj prawidłową cenę brutto'); return false; }
    if (!warehouseId) { toast.error('Nie znaleziono magazynu dla tej lokalizacji.'); return false; }
    const vat = parseFloat(product.stawka_vat);
    const newNetto = (!isNaN(vat) && vat > 0)
      ? Math.round((newBrutto / (1 + vat / 100)) * 100) / 100
      : newBrutto;
    try {
      setLoading(true);
      const response = await warehousePricingService.setWarehousePrice(warehouseId, product.id, {
        cena_sprzedazy_netto: newNetto, cena_sprzedazy_brutto: newBrutto, created_by: 'user_inline'
      });
      if (response.success) {
        await refreshAllData();
        toast.success('Cena specjalna zaktualizowana');
        return true;
      }
      toast.error(`Błąd aktualizacji ceny: ${response.error || 'Nieznany błąd'}`);
      return false;
    } catch (err) {
      toast.error(`Błąd podczas aktualizacji ceny: ${err.message}`);
      return false;
    } finally { setLoading(false); }
  };

  // --- Manufacturer/Unit bulk handlers ---
  const handleSingleManufacturerChange = async (productId, newManufacturerId) => {
    try {
      await manufacturerService.updateProductManufacturer(productId, newManufacturerId);
      loadAllProducts(); setEditingProductManufacturer(null);
    } catch (err) { toast.error('Błąd podczas zmiany producenta'); }
  };

  const handleBulkManufacturerChange = async () => {
    if (!bulkManufacturerChange || selectedProducts.size === 0) { toast.error('Wybierz producenta i produkty do zmiany'); return; }
    try {
      await manufacturerService.bulkUpdateProductManufacturer(Array.from(selectedProducts), bulkManufacturerChange);
      loadAllProducts(); setSelectedProducts(new Set()); setBulkManufacturerChange(''); setShowManufacturerModal(false);
      toast.success('Producenci zostali zaktualizowani');
    } catch (err) { toast.error('Błąd podczas zmiany producentów'); }
  };

  const handleBulkEdit = async () => {
    if (selectedProducts.size === 0) { toast.error('Wybierz produkty do edycji'); return; }
    if (!bulkManufacturerChange && !bulkUnitChange && !bulkQuantityChange) { toast.error('Wybierz producenta, jednostkę lub ilość do zmiany'); return; }
    try {
      const selectedProductIds = Array.from(selectedProducts);
      if (bulkManufacturerChange) await manufacturerService.bulkUpdateProductManufacturer(selectedProductIds, bulkManufacturerChange);
      if (bulkUnitChange || bulkQuantityChange) {
        for (const productId of selectedProductIds) {
          const updateData = {};
          if (bulkUnitChange) updateData.jednostka_wagi = bulkUnitChange;
          if (bulkQuantityChange) updateData.ilosc_jednostek = parseInt(bulkQuantityChange) || null;
          if (Object.keys(updateData).length > 0) await productService.updateProduct(productId, updateData);
        }
      }
      await loadAllProducts(true);
      setSelectedProducts(new Set()); setBulkManufacturerChange(''); setBulkUnitChange(''); setBulkQuantityChange(''); setShowBulkEditModal(false);
      toast.success(`Zaktualizowano ${selectedProductIds.length} produktów`);
    } catch (err) { toast.error('Błąd podczas zbiorczej edycji'); }
  };

  // --- Bulk simplify names ---
  const handleBulkSimplifyNames = async (force = false) => {
    if (!selectedLocation) { toast.error('Proszę najpierw wybrać magazyn'); return; }
    try {
      const allCenowkiResponse = await cenowkiService.getAllCenowki();
      const allCenowki = allCenowkiResponse?.data || allCenowkiResponse || [];

      const cenowkiByProduct = {};
      for (const c of allCenowki) {
        cenowkiByProduct[c.product_id] = c;
      }

      const products = selectedProducts.size > 0
        ? getFilteredProducts().filter(p => selectedProducts.has(p.id))
        : getFilteredProducts();
      console.log('[BulkSimplify] selectedProducts:', [...selectedProducts], 'filtered products count:', products.length, 'cenowki count:', allCenowki.length, 'force:', force);
      if (products.length > 0) console.log('[BulkSimplify] first product:', { id: products[0].id, name: products[0].name, nazwa: products[0].nazwa });
      if (allCenowki.length > 0) console.log('[BulkSimplify] cenowki product_ids:', allCenowki.map(c => c.product_id));
      const itemsToUpdate = [];
      for (const product of products) {
        const cenowka = cenowkiByProduct[product.id];
        if (!cenowka) { console.log('[BulkSimplify] NO cenowka for product', product.id, product.name || product.nazwa); continue; }
        const productName = product.name || product.product_name || product.nazwa || '';
        const simplified = simplifyProductName(productName, manufacturers);
        console.log('[BulkSimplify] product', product.id, '→ simplified:', simplified, '| current:', cenowka.nazwa_uproszczona);
        if (!simplified) continue;
        if (force) {
          // Wymuś — nadpisz WSZYSTKIE (nawet ręcznie edytowane), reset do auto-uproszczonej
          itemsToUpdate.push({ cenowka_id: cenowka.id, nazwa_uproszczona: simplified });
        } else {
          // Normalnie — uprość tylko jeśli wynik różni się od aktualnej nazwy
          if (simplified !== cenowka.nazwa_uproszczona) {
            itemsToUpdate.push({ cenowka_id: cenowka.id, nazwa_uproszczona: simplified });
          }
        }
      }

      if (itemsToUpdate.length === 0) {
        toast('Nie znaleziono cenówek do uproszczenia', { icon: 'ℹ️' });
        return;
      }

      await cenowkiService.bulkSimplifyNames(itemsToUpdate);
      toast.success(`${force ? 'Wymuszono uproszczenie' : 'Uproszczono'} ${itemsToUpdate.length} nazw cenówek`);
      await loadAllProducts(true);
    } catch (err) {
      toast.error('Błąd upraszczania nazw: ' + (err.message || ''));
    }
  };

  // --- Preview and print ---
  const handlePreviewLabels = async () => {
    const selectedProductsList = getFilteredProducts().filter(p => selectedProducts.has(p.id));
    if (!selectedLocation) { toast.error('Proszę najpierw wybrać magazyn'); return; }

    const enrichedProducts = await Promise.all(selectedProductsList.map(async (product) => {
      const priceInfo = getDisplayPrice(product);
      let finalName = product.nazwa_uproszczona || simplifyProductName(product.nazwa, manufacturers);
      try {
        const locationIdVal = selectedLocation?.location_id || selectedLocation;
        const cenowkaResponse = await cenowkiService.getCenowkaByProduct(product.id, locationIdVal);
        if (cenowkaResponse?.data?.nazwa_uproszczona) finalName = cenowkaResponse.data.nazwa_uproszczona;
      } catch (e) {}
      return {
        ...product, simplifiedName: finalName, finalName: finalName,
        packageQuantity: extractPackageQuantity(product, product.nazwa, product.opis, measureUnits),
        weight: extractWeight(product.nazwa, product.opis),
        manufacturer: product.producent || extractManufacturer(product.nazwa, null, manufacturers),
        displayPrice: priceInfo.price
      };
    }));

    setPreviewProducts(enrichedProducts); setSelectedForCopy(new Set()); setShowPreview(true);
  };

  const handlePrintLabels = () => {
    const labelsToShow = [];

    const processProduct = (product, isPreview) => {
      try {
        const displayPrice = parseFloat(product.displayPrice || product.cena_sprzedazy_brutto || 0);
        const { packageInfo, unitPriceText } = computeUnitPriceInfo(product, displayPrice, measureUnits);
        const mfr = product.manufacturer || extractManufacturer(product.nazwa, null, manufacturers);
        const copies = selectedForCopy.has(product.id) ? copyMultiplier : 1;

        for (let i = 0; i < copies; i++) {
          labelsToShow.push({
            price: `${displayPrice.toFixed(2)} zł`,
            productName: product.finalName || product.simplifiedName || product.nazwa || 'Brak nazwy',
            // packageInfo (np. "30 kaps") i unitPriceText (np. "0.43 zl/kaps") ODDZIELNIE
            // - dolny wiersz uklada je w kolumnie (ilosc nad cena za szt.), zeby
            // producent zmiescil sie w 1 linii obok. Auto-skala dba, by nic nie ucielo.
            manufacturer: mfr, packageInfo: packageInfo || '', unitPriceText: unitPriceText || ''
          });
        }
      } catch (e) { console.error('Błąd podczas przetwarzania produktu:', e); }
    };

    if (showPreview && previewProducts.length > 0) {
      previewProducts.forEach(p => processProduct(p, true));
    } else {
      if (!allProducts?.length) { toast.error('Brak produktów do druku.'); return; }
      if (!selectedProducts?.size) { toast.error('Nie wybrano żadnych produktów do druku!'); return; }
      selectedProducts.forEach(id => {
        const product = allProducts.find(p => p.id === id);
        if (product) processProduct(product, false);
      });
    }

    if (labelsToShow.length === 0) { toast.error('Nie udało się przygotować cenówek do druku!'); return; }

    try {
      const printWindow = window.open('', '_blank');
      if (!printWindow) { toast.error('Nie można otworzyć okna drukowania.'); return; }

      // XSS: okno druku dziedziczy origin aplikacji, wiec kazda wartosc z danych
      // (nazwa produktu/producenta z bazy) MUSI byc escapowana przed wstawieniem do HTML.
      const escapeHtml = (v) => String(v ?? '')
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

      const printContent = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Cenówki do druku</title>
        <style>@page { size: A4; margin: 5mm; } body { margin: 0; padding: 5mm; font-family: Arial, sans-serif; background: white; }
        .labels-container { display: flex; flex-wrap: wrap; gap: 2mm; justify-content: flex-start; }
        .price-label { width: 4cm; height: 2cm; border: 1px solid #000; padding: 0; display: flex; flex-direction: column; justify-content: center; text-align: center; background: white; box-sizing: border-box; page-break-inside: avoid; overflow: hidden; }
        .label-inner { width: 100%; display: flex; flex-direction: column; align-items: stretch; transform-origin: center center; }
        .label-inner > div { padding: 0.35mm 1mm; line-height: 1.05; }
        .label-inner > div:not(:last-child) { border-bottom: 1px solid #000; }
        .g-price { font-size: ${labelSettings.priceFontSize}px; font-weight: bold; color: #000; }
        .g-name { font-size: ${labelSettings.productNameFontSize}px; font-weight: bold; color: #000; word-break: break-word; }
        .g-mfr { font-size: ${labelSettings.manufacturerFontSize}px; font-weight: bold; color: #1d4ed8; white-space: nowrap; overflow: hidden; }
        .g-units { padding: 0; display: flex; align-items: stretch; }
        .g-units > div { flex: 1; padding: 0.3mm 1mm; font-size: ${labelSettings.unitsFontSize}px; font-weight: 600; color: #000; white-space: nowrap; line-height: 1.05; }
        .g-units > div:not(:last-child) { border-right: 1px solid #000; }
        @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }</style></head>
        <body><div class="labels-container">${labelsToShow.map(label => {
          const hasMfr = labelSettings.includeManufacturer && label.manufacturer;
          const hasUnits = !!(label.packageInfo || label.unitPriceText);
          return `
          <div class="price-label"><div class="label-inner">
            ${labelSettings.includePrice ? `<div class="g-price">${escapeHtml(label.price)}</div>` : ''}
            ${labelSettings.includeSimplifiedName ? `<div class="g-name">${escapeHtml(label.productName)}</div>` : ''}
            ${hasMfr ? `<div class="g-mfr">${escapeHtml(label.manufacturer)}</div>` : ''}
            ${hasUnits ? `<div class="g-units">${label.packageInfo ? `<div>${escapeHtml(label.packageInfo)}</div>` : ''}${label.unitPriceText ? `<div>${escapeHtml(label.unitPriceText)}</div>` : ''}</div>` : ''}
          </div></div>`;}).join('')}
        </div><script>
          // Auto-dopasowanie: zmniejsz tresc TYLKO gdy nie miesci sie w etykiecie 4x2cm
          // (nic sie nie ucina - nazwa/producent/przelicznik zawsze widoczne w calosci).
          function fitLabels(){
            document.querySelectorAll('.price-label').forEach(function(lab){
              var inner=lab.querySelector('.label-inner'); if(!inner) return;
              inner.style.transform='';
              var cs=getComputedStyle(lab);
              var availH=lab.clientHeight-parseFloat(cs.paddingTop)-parseFloat(cs.paddingBottom);
              var availW=lab.clientWidth-parseFloat(cs.paddingLeft)-parseFloat(cs.paddingRight);
              var s=Math.min(availH/inner.scrollHeight, availW/inner.scrollWidth, 1);
              if(s<1) inner.style.transform='scale('+s+')';
            });
          }
          window.onload=function(){ fitLabels(); setTimeout(function(){window.print();window.close();},400); };
        </script></body></html>`;

      printWindow.document.write(printContent);
      printWindow.document.close();
    } catch (err) { toast.error('Błąd: ' + err.message); }
  };

  // --- Render ---
  if (loading && !selectedLocation) {
    return (
      <div className="container-fluid py-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Ładowanie...</span>
          </div>
          <p className="mt-2">Ładowanie magazynów...</p>
        </div>
      </div>
    );
  }

  const filteredProducts = getFilteredProducts();

  return (
    <div style={{ padding: "0.75rem", fontSize: "12px" }}>
      {/* Header */}
      <div className="card" style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h2 style={{ fontSize: '16px', fontWeight: 600, marginBottom: '4px' }}>Cenówki</h2>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
              {selectedLocation ? selectedLocation.nazwa : 'Wybierz lokalizację'} | {new Date().toLocaleDateString()}
            </p>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
            {selectedProducts.size > 0 && (
              <>
                <span style={{ padding: "0.25rem 0.5rem", fontSize: "10px", fontWeight: "600", backgroundColor: "var(--border)", borderRadius: "4px" }}>
                  {selectedProducts.size} zaznaczonych
                </span>
                {canEditPrices && (
                  <button className="btn btn-primary" style={{ fontSize: "11px", padding: "4px 10px" }}
                    onClick={handleBulkPriceChange} title="Zmień ceny zaznaczonych produktów">
                    <FaEdit style={{ marginRight: "4px" }} />Edytuj ceny
                  </button>
                )}
                {canEditPrices && (
                  <button className="btn btn-primary" style={{ fontSize: "11px", padding: "4px 10px", backgroundColor: "var(--purple)", borderColor: "var(--purple)" }}
                    onClick={() => setShowBulkEditModal(true)} title="Zbiorcza edycja producenta i jednostki">
                    <FaEdit style={{ marginRight: "4px" }} />Producent/Jednostka
                  </button>
                )}
                {canPrintLabels && (
                  <button className="btn btn-primary" style={{ fontSize: "11px", padding: "4px 10px", backgroundColor: "var(--info)", borderColor: "var(--info)" }}
                    onClick={handlePreviewLabels} title="Podgląd cenówek">
                    <FaEye style={{ marginRight: "4px" }} />Podgląd
                  </button>
                )}
                {canDeleteSpecialPrice && (
                  <button className="btn btn-primary" style={{ fontSize: "11px", padding: "4px 10px", backgroundColor: "var(--danger)", borderColor: "var(--danger)" }}
                    onClick={handleRemoveSpecialPrices} title="Usuń ceny specjalne">
                    <FaTrash style={{ marginRight: "4px" }} />Usuń specjalne
                  </button>
                )}
              </>
            )}
            {canPrintLabels && (
              <>
                <div className="btn-group" style={{ position: 'relative' }}>
                  <button className="btn" style={{
                    padding: "4px 10px", fontSize: "11px", fontWeight: "500",
                    border: "1px solid var(--info)", borderRadius: "4px 0 0 4px", cursor: "pointer",
                    color: "var(--info)"
                  }} onClick={() => handleBulkSimplifyNames(false)} title="Uprość nazwy cenówek (usuń producenta i smak)">
                    <FaMagic style={{ marginRight: "4px" }} />Uprość nazwy
                  </button>
                  <button className="btn" style={{
                    padding: "4px 10px", fontSize: "11px", fontWeight: "500",
                    border: "1px solid var(--warning)", borderLeft: "none", borderRadius: "0 4px 4px 0", cursor: "pointer",
                    color: "var(--warning)"
                  }} onClick={() => { if (window.confirm('Wymusić ponowne uproszczenie WSZYSTKICH nazw? Nadpisze ręcznie edytowane nazwy.')) handleBulkSimplifyNames(true); }} title="Wymuś ponowne uproszczenie — nadpisze nawet ręcznie edytowane nazwy">
                    <FaRedo size={10} />
                  </button>
                </div>
                <button className="btn" style={{
                  padding: "4px 10px", fontSize: "11px", fontWeight: "500",
                  border: "1px solid var(--border)", cursor: "pointer",
                  backgroundColor: labelBuffer.length > 0 ? "var(--warning)" : "var(--card-bg)",
                  color: "var(--text)"
                }} onClick={() => setShowBuffer(true)} title={`Bufor cenówek (${labelBuffer.length})`}>
                  <FaTag style={{ marginRight: "4px" }} />Bufor ({labelBuffer.length})
                </button>
              </>
            )}
            <button className="btn btn-primary" style={{ fontSize: "11px", padding: "4px 10px" }}
              onClick={loadAllProducts} disabled={loading}>
              {loading ? "..." : "Odśwież"}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ backgroundColor: 'var(--danger-light)', color: 'var(--danger)', padding: '0.5rem 0.75rem', borderRadius: '4px', marginBottom: '0.75rem', fontSize: '11px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>{'❌'} {error}</span>
          <button onClick={() => setError('')} style={{ background: 'none',  cursor: 'pointer', fontSize: '14px' }}>{'×'}</button>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem", flexWrap: "wrap" }}>
        <div style={{ backgroundColor: "var(--card-bg)", borderLeft: "3px solid var(--purple)", borderRadius: "4px", padding: "0.5rem 0.75rem", textAlign: "center", minWidth: "100px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--purple)" }}>{filteredProducts.length}</div>
          <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Produkty</div>
        </div>
        <div style={{ backgroundColor: "var(--card-bg)", borderLeft: "3px solid var(--success)", borderRadius: "4px", padding: "0.5rem 0.75rem", textAlign: "center", minWidth: "100px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--success)" }}>{filteredProducts.filter(p => p.hasSpecialPrice).length}</div>
          <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Ceny specjalne</div>
        </div>
        {/* Klik = filtr "ceny specjalne do uzupełnienia" (toggle). Liczy produkty bez ceny specjalnej w aktualnym widoku. */}
        {(() => {
          const toFill = filteredProducts.filter(p => !p.hasSpecialPrice).length;
          const active = advancedFilters.priceType === 'default';
          return (
            <div
              onClick={() => { setAdvancedFilters(prev => ({ ...prev, priceType: prev.priceType === 'default' ? 'all' : 'default' })); setCurrentPage(1); }}
              title="Kliknij, aby pokazać produkty z cenami specjalnymi do uzupełnienia"
              style={{ cursor: "pointer", backgroundColor: active ? "var(--danger-light)" : (toFill > 0 ? "var(--danger-light)" : "var(--card-bg)"), borderLeft: "3px solid var(--danger)", borderRadius: "4px", padding: "0.5rem 0.75rem", textAlign: "center", minWidth: "150px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
              <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--danger)" }}>{toFill}</div>
              <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Ceny specjalne do uzupełnienia</div>
              {toFill > 0 && (
                <div style={{ fontSize: "9px", fontWeight: "700", color: "var(--danger)", marginTop: "2px" }}>⚠ Koniecznie uzupełnij!</div>
              )}
            </div>
          );
        })()}
        <div style={{ backgroundColor: "var(--card-bg)", borderLeft: "3px solid var(--warning)", borderRadius: "4px", padding: "0.5rem 0.75rem", textAlign: "center", minWidth: "100px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize: "16px", fontWeight: "700", color: "var(--warning)" }}>{selectedProducts.size}</div>
          <div style={{ fontSize: "10px", color: "var(--text-muted)" }}>Zaznaczone</div>
        </div>
        <div style={{ backgroundColor: "var(--card-bg)", borderLeft: "3px solid var(--cyan)", borderRadius: "4px", padding: "0.5rem 0.75rem", minWidth: "200px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <div style={{ fontSize: "10px", color: "var(--text-muted)", marginBottom: "4px" }}>📍 Lokalizacja</div>
          <select style={{ width: "100%", padding: "6px 10px", fontSize: "12px", border: "1px solid var(--border)", borderRadius: "6px", backgroundColor: "var(--card-bg)", color: "var(--text)" }}
            value={selectedLocation?.id || ''} onChange={(e) => { const locationId = parseInt(e.target.value); changeLocation(locationId); }}>
            <option value="">-- Wybierz --</option>
            {availableLocations.map((loc) => (<option key={loc.id} value={loc.id}>{loc.nazwa}</option>))}
          </select>
        </div>
      </div>

      {/* Main content */}
      {selectedLocation ? (
        <div style={{ backgroundColor: "var(--card-bg)", borderRadius: "4px", boxShadow: "0 1px 3px rgba(0,0,0,0.08)", overflow: "hidden" }}>
          <PricingFilters
            invoices={invoices} selectedInvoiceId={selectedInvoiceId} handleInvoiceFilter={handleInvoiceFilter}
            invoiceLoading={invoiceLoading} invoiceEANs={invoiceEANs}
            scannerInputRef={scannerInputRef} barcodeInput={barcodeInput} setBarcodeInput={setBarcodeInput}
            handleBarcodeInput={handleBarcodeInput} handlePasteBarcodes={handlePasteBarcodes}
            scannerFilterActive={scannerFilterActive} setScannerFilterActive={setScannerFilterActive}
            scannedBarcodes={scannedBarcodes} handleClearAllBarcodes={handleClearAllBarcodes}
            handleAddScannedToBuffer={handleAddScannedToBuffer} handleRemoveBarcode={handleRemoveBarcode}
            allProducts={allProducts}
            filterTerm={filterTerm} setFilterTerm={setFilterTerm}
            pokazBezStanu={pokazBezStanu} setPokazBezStanu={przelaczBezStanu} ladowanieProduktow={loading}
            categories={categories} selectedCategory={selectedCategory} setSelectedCategory={setSelectedCategory}
            manufacturers={manufacturers} selectedManufacturer={selectedManufacturer} setSelectedManufacturer={setSelectedManufacturer}
            advancedFilters={advancedFilters} setAdvancedFilters={setAdvancedFilters}
            handleSelectAll={handleSelectAll} setSelectedProducts={setSelectedProducts}
            setCurrentPage={setCurrentPage} filteredProductsCount={filteredProducts.length}
          />
          <PricingTable
            filteredProducts={filteredProducts}
            currentPage={currentPage} setCurrentPage={setCurrentPage}
            itemsPerPage={itemsPerPage} setItemsPerPage={setItemsPerPage}
            sortColumn={sortColumn} sortDirection={sortDirection} handleSort={handleSort}
            selectedProducts={selectedProducts} handleSelectProduct={handleSelectProduct} handleSelectAll={handleSelectAll}
            manufacturers={manufacturers} measureUnits={measureUnits}
            editingProductManufacturer={editingProductManufacturer} setEditingProductManufacturer={setEditingProductManufacturer}
            handleSingleManufacturerChange={handleSingleManufacturerChange}
            editingCenowkaProductId={editingCenowkaProductId}
            openActionMenu={openActionMenu} setOpenActionMenu={setOpenActionMenu}
            canEditPrices={canEditPrices} canPrintLabels={canPrintLabels}
            canViewHistory={canViewHistory} canDeleteSpecialPrice={canDeleteSpecialPrice}
            handleShowHistory={handleShowHistory} handleDirectPriceEdit={handleDirectPriceEdit}
            handleInlineSpecialPriceSave={handleInlineSpecialPriceSave}
            handleEditCenowka={handleEditCenowka} handleBulkPriceChange={handleBulkPriceChange}
            setSelectedProducts={setSelectedProducts} handleRemoveSpecialPrices={handleRemoveSpecialPrices}
            addToBuffer={addToBuffer} selectedInvoiceId={selectedInvoiceId} filterTerm={filterTerm}
            cenowkaEditorProps={{
              cenowkaEditData, setCenowkaEditData,
              handleSaveCenowka, handleCancelCenowkaEdit,
              addToBuffer, editingCenowkaProductId, canPrintLabels,
              showPriceCalculator, setShowPriceCalculator,
              calculatedPrices, setPriceFromCalculator,
              getFilteredProducts,
            }}
          />
        </div>
      ) : (
        <div style={{ backgroundColor: "var(--card-bg)", borderRadius: "4px", padding: "2rem", textAlign: "center", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
          <FaStore style={{ fontSize: "2rem", color: "var(--text-muted)", marginBottom: "0.5rem" }} />
          <div style={{ fontSize: "13px", fontWeight: "500", color: "var(--text-secondary)" }}>Wybierz lokalizację</div>
          <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>Wybierz lokalizację z listy powyżej aby zobaczyć produkty</div>
        </div>
      )}

      {/* Modals */}
      <PriceChangeModal show={showPriceModal} onClose={() => setShowPriceModal(false)}
        selectedProducts={selectedProducts} priceChangeMode={priceChangeMode} setPriceChangeMode={setPriceChangeMode}
        priceChangeValue={priceChangeValue} setPriceChangeValue={setPriceChangeValue}
        roundToPsychological={roundToPsychological} setRoundToPsychological={setRoundToPsychological}
        onApply={applyPriceChange} loading={loading} />

      <PriceHistoryModal show={showHistoryModal} product={selectedProductForHistory}
        priceHistory={priceHistory} onClose={() => setShowHistoryModal(false)} />

      <DirectPriceModal show={showDirectPriceModal} product={selectedProductForDirectEdit}
        onClose={() => setShowDirectPriceModal(false)}
        directPriceNetto={directPriceNetto} setDirectPriceNetto={setDirectPriceNetto}
        directPriceBrutto={directPriceBrutto} setDirectPriceBrutto={setDirectPriceBrutto}
        onApply={applyDirectPriceChange} loading={loading} />

      <PriceLabelsPreview show={showPreview} onClose={() => setShowPreview(false)}
        previewProducts={previewProducts}
        labelSettings={labelSettings} setLabelSettings={setLabelSettings}
        settingsSaved={settingsSaved} handleSaveSettings={handleSaveSettings}
        copyMultiplier={copyMultiplier} setCopyMultiplier={setCopyMultiplier}
        selectedForCopy={selectedForCopy} handleToggleCopySelection={handleToggleCopySelection}
        handleSelectAllForCopy={handleSelectAllForCopy} handleDeselectAllForCopy={handleDeselectAllForCopy}
        labelBuffer={labelBuffer} handleLoadBufferToPreview={handleLoadBufferToPreview}
        addToBuffer={addToBuffer} setShowBuffer={setShowBuffer} setShowPreview={setShowPreview}
        handlePrintLabels={handlePrintLabels} measureUnits={measureUnits} />

      <BufferPanel show={showBuffer} onClose={() => setShowBuffer(false)}
        labelBuffer={labelBuffer} removeFromBuffer={removeFromBuffer} clearBuffer={clearBuffer}
        handleTransferBufferToPreview={handleTransferBufferToPreview}
        manufacturers={manufacturers} measureUnits={measureUnits} />

      <ManufacturerModal show={showManufacturerModal} onClose={() => setShowManufacturerModal(false)}
        selectedProducts={selectedProducts} manufacturers={manufacturers}
        bulkManufacturerChange={bulkManufacturerChange} setBulkManufacturerChange={setBulkManufacturerChange}
        onApply={handleBulkManufacturerChange} />

      <BulkEditModal show={showBulkEditModal} onClose={() => setShowBulkEditModal(false)}
        selectedProducts={selectedProducts} manufacturers={manufacturers} measureUnits={measureUnits}
        bulkManufacturerChange={bulkManufacturerChange} setBulkManufacturerChange={setBulkManufacturerChange}
        bulkUnitChange={bulkUnitChange} setBulkUnitChange={setBulkUnitChange}
        bulkQuantityChange={bulkQuantityChange} setBulkQuantityChange={setBulkQuantityChange}
        onApply={handleBulkEdit} filteredProducts={filteredProducts} />
    </div>
  );
};

export default LocationPricingPage;
